import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useHseInstance } from "./useHseInstance";
import { announcementReactionsOptions, announcementsOptions } from "@dashboard/api/queryApi";
import {
    createAnnouncement,
    deleteAnnouncement,
    updateAnnouncement,
    setAnnouncementReaction,
    deleteAnnouncementReaction,
    type Announcement,
    type AnnouncementListResponse,
    type CreateAnnouncementInput,
    type UpdateAnnouncementInput,
} from "@dashboard/api/task";
import type { ReactionType } from "frontend-components";

function replaceAnnouncementInList(
    current: AnnouncementListResponse | undefined,
    updatedAnnouncement: Announcement,
): AnnouncementListResponse | undefined {
    if (!current) {
        return current;
    }

    return {
        ...current,
        data: current.data.map((announcement) =>
            announcement.id === updatedAnnouncement.id ? updatedAnnouncement : announcement,
        ),
    };
}

export function useAnnouncements() {
    const { instanceId } = useHseInstance();
    return useQuery(announcementsOptions({ instanceId }));
}

export function useAnnouncementReactions(id: number, enabled = true) {
    return useQuery({ ...announcementReactionsOptions(id), enabled });
}

export function useCreateAnnouncement() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data: CreateAnnouncementInput) => createAnnouncement(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["announcements"] });
        },
    });
}

export function useUpdateAnnouncement() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: UpdateAnnouncementInput }) =>
            updateAnnouncement(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["announcements"] });
        },
    });
}

export function useDeleteAnnouncement() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: number) => deleteAnnouncement(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["announcements"] });
        },
    });
}

export function useSetAnnouncementReaction() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, type }: { id: number; type: ReactionType }) =>
            setAnnouncementReaction(id, type),
        onSuccess: (updatedAnnouncement, { id }) => {
            queryClient.setQueriesData<AnnouncementListResponse>(
                { queryKey: ["announcements"] },
                (current) => replaceAnnouncementInList(current, updatedAnnouncement),
            );
            void queryClient.invalidateQueries(announcementReactionsOptions(id));
        },
    });
}

export function useDeleteAnnouncementReaction() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: number) => deleteAnnouncementReaction(id),
        onSuccess: (updatedAnnouncement, id) => {
            queryClient.setQueriesData<AnnouncementListResponse>(
                { queryKey: ["announcements"] },
                (current) => replaceAnnouncementInList(current, updatedAnnouncement),
            );
            void queryClient.invalidateQueries(announcementReactionsOptions(id));
        },
    });
}
